import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SiteFeatures } from 'src/app/bo/siteFeatures';
import { SitesService } from 'src/app/services/sites.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';

interface FeatureRow {
  key: keyof SiteFeatures;
  translateKey: string;
}

const FEATURE_ROWS: FeatureRow[] = [
  { key: 'showBlastDesignTab',         translateKey: 'FEATURES.BLAST_DESIGN' },
  { key: 'showBlastLoadTab',           translateKey: 'FEATURES.BLAST_LOAD' },
  { key: 'show3dViewerTab',            translateKey: 'FEATURES.3D_VIEWER' },
  { key: 'showDeliveriesTab',          translateKey: 'FEATURES.DELIVERIES' },
  { key: 'showVibrationsTab',          translateKey: 'FEATURES.VIBRATIONS' },
  { key: 'showFragmentationTab',       translateKey: 'FEATURES.FRAGMENTATION' },
  { key: 'showLoadAndHaulTab',         translateKey: 'FEATURES.LOAD_AND_HAUL' },
  { key: 'showFleetManagementSystemTab', translateKey: 'FEATURES.FLEET_MANAGEMENT' },
  { key: 'showEnvironmentTab',         translateKey: 'FEATURES.ENVIRONMENT' },
];

@Component({
  selector: 'app-site-features',
  templateUrl: './site-features.component.html',
  styleUrls: ['./site-features.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule],
})
export class SiteFeaturesComponent {

  protected readonly utilService = inject(UtilisateurService);
  private readonly sitesService = inject(SitesService);

  protected readonly features = signal<SiteFeatures>(new SiteFeatures());
  protected readonly isLoading = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly saveSuccess = signal(false);

  protected readonly featureRows = FEATURE_ROWS;

  constructor() {
    effect(() => {
      const site = this.utilService.currentSite();
      if (site?.siteId) {
        this.loadFeatures(site.siteId);
      }
    });
  }

  private loadFeatures(siteId: number): void {
    this.isLoading.set(true);
    this.error.set(null);
    this.sitesService.getSiteFeatures(siteId).subscribe({
      next: data => {
        this.features.set(data);
        this.isLoading.set(false);
      },
      error: () => {
        this.error.set('ERROR.LOAD_FAILED');
        this.isLoading.set(false);
      },
    });
  }

  protected toggle(key: keyof SiteFeatures): void {
    const current = this.features();
    this.features.set({ ...current, [key]: !current[key] });
  }

  protected save(): void {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) return;

    this.isSaving.set(true);
    this.saveSuccess.set(false);
    this.error.set(null);

    this.sitesService.updateSiteFeatures(siteId, this.features()).subscribe({
      next: updated => {
        this.features.set(updated);
        this.isSaving.set(false);
        this.saveSuccess.set(true);
        setTimeout(() => this.saveSuccess.set(false), 3000);

        // Patch currentSite signal so tir-detail reacts immediately
        const site = this.utilService.currentSite();
        if (site) {
          site.features = updated;
          this.utilService.setCurrentSite(site);
        }
      },
      error: () => {
        this.error.set('ERROR.SAVE_FAILED');
        this.isSaving.set(false);
      },
    });
  }
}
